import { useState, useEffect, Link, useSearchParams, motion, axios, Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Badge, Loader2, FileText, TrendingUp, Users, Gift, Sparkles, CalendarDays, Link2, ChevronRight, Zap, Check, X, API, useAuth, toast } from "@/pages/shared";
import { ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CLAIMS } from "@/config/claims";

export default function DashboardHome() {
  const { user, token, updateUser } = useAuth();
  const [stats, setStats] = useState({ total_content: 0, total_clicks: 0, total_leads: 0, plan: "free", referral_credits: 0 });
  const [viralStats, setViralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // ClarityDoc cross-sell — dismissible, persists across visits via localStorage
  const [clarityDismissed, setClarityDismissed] = useState(() => localStorage.getItem("claritydoc_promo_dismissed") === "1");
  const dismissClarity = () => { localStorage.setItem("claritydoc_promo_dismissed", "1"); setClarityDismissed(true); };

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const fetchStats = async () => {
      try { const response = await axios.get(`${API}/dashboard/stats`, { headers }); setStats(response.data); } catch (e) { console.error(e); }
      setLoading(false);
    };
    const fetchViralStats = async () => {
      try { const res = await axios.get(`${API}/viral/stats`, { headers }); setViralStats(res.data); } catch (e) { console.error(e); }
    };
    const checkPayment = async () => {
      const sessionId = searchParams.get("session_id"), paymentStatus = searchParams.get("payment");
      if (sessionId && paymentStatus === "success") {
        // Immediately clear the query params so this effect can never re-fire for this session.
        navigate("/dashboard", { replace: true });
        try {
          const response = await axios.get(`${API}/payments/status/${sessionId}`, { headers });
          if (response.data.payment_status === "paid") {
            toast.success("Payment successful!");
            const userResponse = await axios.get(`${API}/auth/me`, { headers });
            updateUser(userResponse.data);

            // Yandex.Metrika revenue goal — fires once per successful Stripe checkout
            try {
              const plan = (userResponse.data.plan || "").toLowerCase();
              const goalName = plan === "enterprise" ? "enterprise_upgrade" : "pro_upgrade";
              const revenue = plan === "enterprise" ? 99 : 29;
              if (typeof window !== "undefined" && typeof window.ym === "function") {
                window.ym(109148690, "reachGoal", goalName, {
                  order_id: sessionId,
                  revenue,
                  currency: "GBP",
                  plan,
                });
              }
              // Google Analytics 4 ecommerce event (no-op if gtag is not yet installed)
              if (typeof window !== "undefined" && typeof window.gtag === "function") {
                window.gtag("event", "purchase", {
                  transaction_id: sessionId,
                  value: revenue,
                  currency: "GBP",
                  items: [{ item_id: plan, item_name: `Creatara ${plan}`, price: revenue, quantity: 1 }],
                });
              }
            } catch (analyticsErr) {
              console.warn("Analytics goal fire failed (non-fatal):", analyticsErr);
            }
          }
        } catch (e) { console.error(e); }
      }
    };
    fetchStats(); fetchViralStats(); checkPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchParams]);

  const statCards = [
    { title: "Content Generated", value: stats.total_content, icon: FileText, color: "text-violet-500" },
    { title: "Affiliate Clicks", value: stats.total_clicks, icon: TrendingUp, color: "text-teal-500" },
    { title: "Leads Captured", value: stats.total_leads, icon: Users, color: "text-blue-500" },
    { title: "Referral Credits", value: stats.referral_credits, icon: Gift, color: "text-amber-500" }
  ];

  return (
    <div className="space-y-8">
      <div><h1 className="text-3xl font-bold mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h1><p className="text-muted-foreground">Here's your dashboard overview.</p></div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {statCards.map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
            <Card className="glass card-hover" data-testid={`stat-card-${i}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4"><stat.icon className={`w-8 h-8 ${stat.color}`} /></div>
                <p className="text-3xl font-bold mb-1">{loading ? "-" : stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="glass">
          <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {[
              { to: "/dashboard/content", icon: Sparkles, label: "Generate AI Content", color: "text-violet-500" },
              { to: "/dashboard/campaigns", icon: TrendingUp, label: "Run a Revenue Campaign", color: "text-pink-500" },
              { to: "/dashboard/videos", icon: Sparkles, label: "Create AI Video (Sora 2)", color: "text-purple-500" },
              { to: "/dashboard/calendar", icon: CalendarDays, label: "View Content Calendar", color: "text-teal-500" },
              { to: "/dashboard/affiliates", icon: Link2, label: "Create Affiliate Link", color: "text-blue-500" },
              { to: "/dashboard/referrals", icon: Gift, label: "Share & Earn Credits", color: "text-amber-500" }
            ].map((item, i) => (
              <Link key={i} to={item.to} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3"><item.icon className={`w-5 h-5 ${item.color}`} /><span>{item.label}</span></div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Link>
            ))}
            {/* Inspiration / proof links — public pages, useful to share with prospects */}
            <a href="/case-study" target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="quick-action-case-study">
              <div className="flex items-center gap-3"><Check className="w-5 h-5 text-teal-500" /><span>Example walkthrough: a 30-day content workflow</span></div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </a>
            <a href="/vs" target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="quick-action-comparison">
              <div className="flex items-center gap-3"><Zap className="w-5 h-5 text-amber-500" /><span>Why we beat Jasper & Copy.ai</span></div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </a>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardHeader><CardTitle>Your Plan</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div><p className="text-2xl font-bold capitalize">{user?.plan}</p><p className="text-sm text-muted-foreground">{user?.plan === "free" ? `${CLAIMS.freeAllowance.generations + stats.referral_credits} generations / ${CLAIMS.freeAllowance.period}` : user?.plan === "pro" ? "100/month" : "Unlimited"}</p></div>
              {user?.plan !== "enterprise" && <Link to="/dashboard/settings"><Button className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="upgrade-btn">Upgrade</Button></Link>}
            </div>
          </CardContent>
        </Card>
      </div>

      {!clarityDismissed && (
        <Card className="relative overflow-hidden border-violet-500/30 bg-gradient-to-r from-violet-600/15 via-card to-card" data-testid="claritydoc-promo-card">
          <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-violet-500 to-fuchsia-500" />
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4 flex-1 min-w-[260px]">
                <div className="w-11 h-11 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center shrink-0 shadow-[0_0_18px_rgba(124,92,252,0.25)]">
                  <ShieldCheck className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">Never sign a risky contract again.</p>
                    <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/25 text-[11px] px-2 py-0">From £9.99 · no account needed</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    <span className="text-violet-400 font-medium">ClarityDoc</span> — our sister tool — reads any contract, NDA or agreement and flags the traps in plain English, in 60 seconds.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href="https://claritydoc.co.uk?utm_source=creatara&utm_medium=dashboard_promo" target="_blank" rel="noreferrer">
                  <Button size="sm" className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-5" data-testid="claritydoc-promo-cta">
                    Check a contract <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </a>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground" onClick={dismissClarity} title="Dismiss" data-testid="claritydoc-promo-dismiss">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {viralStats && (
        <Card className="glass border-violet-500/10" data-testid="viral-meter">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> Viral Meter</CardTitle>
            <CardDescription>Track how your content spreads and earns you credits</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-4 rounded-lg bg-white/5">
                <p className="text-3xl font-bold text-amber-500">{viralStats.total_viral_clicks}</p>
                <p className="text-sm text-muted-foreground mt-1">Link Clicks</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-white/5">
                <p className="text-3xl font-bold text-teal-500">{viralStats.total_referrals}</p>
                <p className="text-sm text-muted-foreground mt-1">Signups from You</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-white/5">
                <p className="text-3xl font-bold text-violet-500">{viralStats.total_credits}</p>
                <p className="text-sm text-muted-foreground mt-1">Credits Earned</p>
              </div>
            </div>
            {user?.plan === "free" && (
              <p className="text-xs text-muted-foreground mt-4 text-center">Every post you share includes your referral link. When someone signs up, you earn 5 Pro Credits!</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
